import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { TableVirtuoso } from 'react-virtuoso';
import { Text, Flex, Center, useToken, Table, Tbody, Tr, Th, Td, Thead } from '@chakra-ui/react';
import { forwardRef, Fragment, useCallback, useMemo, useRef } from 'react';
import { hasValue, isObject } from '@progress-fe/core';
import Latex from 'react-latex-next';
import { Checkbox } from '../../inputs';
import { TableEditableValue, TableValue } from '../components';
import { TABLE_VARIANTS } from './VirtualTable.enum';
const VirtualTableFC = ({ isRowHover, isCheckboxShown, noItemsTitle, rows, columns, tableSx = {}, variant, selectedRowIdx, isTableShownWhenEmpty, onSelect, onUnselect, onSelectAll, onUnselectAll, onValueChange = () => { }, onRowClick }) => {
    const [bgHex, borderHex] = useToken('colors', ['bg', 'border']);
    const hiddenColumns = useRef(new Set());
    const visibleColumns = useMemo(() => {
        hiddenColumns.current.clear();
        const visible = [];
        columns.forEach((column, idx) => {
            if (column.hidden) {
                hiddenColumns.current.add(idx);
                return;
            }
            visible.push(column);
        });
        return visible;
    }, [columns]);
    const gridTemplate = useMemo(() => {
        const columnsTemplate = visibleColumns
            .map((column) => column.width ?? `minmax(90px , ${column.columnTemplate ?? '1fr'})`)
            .join(' ');
        return `${isCheckboxShown ? '20px' : ''} ${columnsTemplate}`;
    }, [visibleColumns, isCheckboxShown]);
    const isAllSelected = () => {
        return rows.every((row) => row.isDisabled || row.isSelected);
    };
    const isFirstColumnSticky = variant === TABLE_VARIANTS.FIRST_COLOMN_FIXED;
    const columnFixedPosition = isCheckboxShown ? '24px' : '0px';
    const renderCell = useCallback((col, value, isDisabled, rowindex, cellIndex) => {
        const getRenderOptions = () => {
            if (!hasValue(col.type) && !isObject(value)) {
                return null;
            }
            const cellOption = isObject(value) ? value : {};
            return {
                readOnly: col.readOnly,
                type: col.type,
                options: col.options,
                unit: col.unit,
                ...cellOption
            };
        };
        const cellOptions = getRenderOptions();
        if (!cellOptions?.type) {
            return (_jsx(TableValue, { isFormula: col.formula, isDisabled: isDisabled, value: value, render: col.render }));
        }
        const cellValue = isObject(value) ? value.value : value;
        return (_jsx(TableEditableValue, { isDisabled: cellOptions.readOnly || isDisabled, type: cellOptions.type, options: cellOptions.options, unit: cellOptions.unit, value: cellValue, isInvalid: cellOptions.required && !cellValue, onChangeValue: (newValue) => {
                onValueChange(rowindex, cellIndex, newValue);
            } }));
    }, [onValueChange]);
    const handleOnChecked = (key, isSelected) => {
        if (isSelected) {
            onSelect?.(key);
        }
        else {
            onUnselect?.(key);
        }
    };
    return (_jsxs(Flex, { flexDirection: "column", border: "1px solid", borderColor: "border", borderRadius: "4px", w: "100%", h: "100%", sx: tableSx, overflow: "hidden", position: "relative", children: [(rows.length > 0 || isTableShownWhenEmpty) && (_jsx(TableVirtuoso, { data: rows, style: { flexGrow: 1, overflowY: 'scroll', width: '100%' }, components: {
                    // @ts-ignore
                    Table: ({ style = {}, ...props }, _ref) => (_jsx(Table, { ...props, variant: "unstyled", style: {
                            ...style,
                            width: '100%',
                            background: bgHex
                        } })),
                    // @ts-ignore
                    TableHead: forwardRef(({ style, ...props }, ref) => (_jsx(Thead, { ...props, style: { ...style, zIndex: 2 }, ref: ref }))),
                    // @ts-ignore
                    TableBody: forwardRef(({ style, ...props }, ref) => (_jsx(Tbody, { ...props, style: { ...style, padding: '2px 0' }, ref: ref }))),
                    // @ts-ignore
                    TableRow: forwardRef(({ style, ...props }, _ref) => (_jsx(Tr, { ...props, style: { ...style }, border: "none", display: "grid", gridTemplateColumns: gridTemplate, alignItems: "center", gap: "4px", role: "group", 
                        // @ts-ignore
                        bg: selectedRowIdx === props['data-index'] ? 'accentMid' : undefined, _hover: {
                            background: isRowHover ? 'accentMid' : undefined,
                            cursor: isRowHover ? 'pointer' : undefined
                        }, 
                        // @ts-ignore
                        onClick: () => onRowClick?.(props['data-index']) })))
                }, fixedHeaderContent: () => (_jsxs(Tr, { gap: "4px", borderBottom: "1px solid", borderColor: "border", bg: "bg", display: "grid", gridTemplateColumns: gridTemplate, children: [isCheckboxShown && (_jsx(Th, { pl: "4px", position: isFirstColumnSticky ? 'sticky' : 'static', left: isFirstColumnSticky ? '0' : undefined, minH: "36px", justifyContent: "center", alignContent: "center", children: !!onSelectAll && !!onUnselectAll && (_jsx(Checkbox, { name: "selectAll", checked: isAllSelected(), sx: { pl: '4px' }, onChange: () => {
                                    if (isAllSelected()) {
                                        onUnselectAll();
                                    }
                                    else {
                                        onSelectAll();
                                    }
                                } })) })), visibleColumns.map((column, idx) => (_jsx(Th, { textAlign: column.align ?? 'center', align: "center", minH: "36px", justifyContent: "center", alignContent: "center", bg: "bg", px: "0px", _last: { paddingRight: '4px' }, _first: {
                                paddingLeft: '4px'
                            }, style: idx === 0 && isFirstColumnSticky
                                ? {
                                    borderRight: '1px solid',
                                    borderColor: borderHex,
                                    position: 'sticky',
                                    left: columnFixedPosition,
                                    paddingRight: '2px'
                                }
                                : {}, children: _jsx(Text, { fontSize: "12px", color: "bodyText", style: column.ellipsis
                                    ? {
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                        textOverflow: 'ellipsis'
                                    }
                                    : {}, children: _jsxs(Latex, { children: [column.name, " ", column.unit ? `(${column.unit})` : ''] }) }) }, column.name)))] })), itemContent: (rowindex, { key, values, isSelected, isDisabled }) => {
                    return (_jsxs(_Fragment, { children: [isCheckboxShown && (_jsx(Td, { p: "0px 0px 0px 4px", h: "24px", position: isFirstColumnSticky ? 'sticky' : 'static', left: isFirstColumnSticky ? '0' : undefined, alignContent: "center", children: _jsx(Checkbox, { checked: isSelected, isDisabled: isDisabled, name: `${key}_checkbox`, onChange: (checked) => handleOnChecked(key, checked) }) })), values.map((value, index) => {
                                const cellKey = `${key}_${index}`;
                                return hiddenColumns.current.has(index) ? (_jsx(Fragment, {}, cellKey)) : (_jsx(Td, { h: "24px", className: "table-column", align: "center", boxSizing: "content-box", overflow: "hidden", alignContent: "center", justifyItems: visibleColumns[index]?.align === 'right' ? 'end' : 'start', bg: isRowHover ? undefined : 'bg', p: "2px 0", _last: { paddingRight: '4px' }, _first: {
                                        paddingLeft: '4px'
                                    }, style: index === 0 && isFirstColumnSticky
                                        ? {
                                            borderRight: '1px solid',
                                            borderColor: borderHex,
                                            position: 'sticky',
                                            left: columnFixedPosition,
                                            zIndex: 1,
                                            paddingRight: '2px'
                                        }
                                        : {}, children: renderCell(columns[index], value, !!isDisabled, rowindex, index) }, cellKey));
                            })] }));
                } })), rows.length === 0 && (_jsx(Center, { h: "100%", fontSize: "12px", color: "bodyText", style: isTableShownWhenEmpty
                    ? {
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)'
                    }
                    : {}, children: noItemsTitle }))] }));
};
export const VirtualTable = VirtualTableFC;
//# sourceMappingURL=VirtualTable.js.map